import type { Row, Table } from "@tanstack/react-table";
import { useMemo } from "react";

import type { Matter } from "@/shared/cache/slices/matters";
import type { RestrictedWord } from "@/shared/cache/slices/restrictedWords";
import type { Entry } from "@/shared/types/entries";
import type { FilterState } from "@/v2/components/FilterPopover";
import type { GroupMode, SortMode } from "@/v2/components/SortPopover";
import type { DateRange } from "@/v2/hooks/useDateParams";
import {
	isSameLocalDate,
	isWithinLocalDateRange,
	localDateKey,
	parseEntryDate,
} from "@/v2/lib/dates";
import { useEntriesTable } from "./useEntriesTable";
import {
	type EntriesTableSelectionController,
	useEntriesTableSelectionController,
	useEntriesTableSelectionState,
} from "./useEntriesTableSelection";

export interface UseEntriesPageDataOptions {
	entries: Entry[];
	date: Date;
	dateRange: DateRange;
	filter: FilterState;
	sort: SortMode;
	group: GroupMode;
	matters?: Matter[];
	getRestrictedWords?: (client?: string, matter?: string) => RestrictedWord[];
}

export interface EntriesPageData {
	dateFilteredEntries: Entry[];
	table: Table<Entry>;
	filteredRows: Row<Entry>[];
	isMultiDate: boolean;
	selection: EntriesTableSelectionController;
}

export function useEntriesPageData({
	entries,
	date,
	dateRange,
	filter,
	sort,
	group,
	matters,
	getRestrictedWords,
}: UseEntriesPageDataOptions): EntriesPageData {
	const dateFilteredEntries = useMemo(() => {
		return entries.filter((entry) => {
			const entryDate = parseEntryDate(entry.date);

			if (dateRange.from && dateRange.to) {
				return isWithinLocalDateRange(entryDate, dateRange);
			}

			return isSameLocalDate(entryDate, date);
		});
	}, [entries, date, dateRange]);

	const isMultiDate = !!(dateRange.from && dateRange.to);
	const selectionScopeKey = useMemo(() => {
		const hiddenApps = Array.from(filter.hiddenApps).sort().join(",");
		const statuses = Array.from(filter.statuses).sort().join(",");
		const parts: string[] = [
			localDateKey(date),
			localDateKey(dateRange.from),
			localDateKey(dateRange.to),
			hiddenApps,
			statuses,
		];

		return parts.join("|");
	}, [date, dateRange.from, dateRange.to, filter.hiddenApps, filter.statuses]);

	const {
		rowSelection,
		selectionMode,
		handleRowSelectionChange,
		setSelectedRowIds,
	} = useEntriesTableSelectionState(selectionScopeKey);

	const table = useEntriesTable({
		entries: dateFilteredEntries,
		filter,
		sort,
		group,
		isMultiDate,
		matters,
		getRestrictedWords,
		rowSelection,
		onRowSelectionChange: handleRowSelectionChange,
	});

	const tableRows = table.getRowModel().rows;
	const visibleEntryRows = useMemo(
		() => tableRows.filter((row) => !row.getIsGrouped()),
		[tableRows],
	);
	const filteredRows = table.getFilteredRowModel().flatRows;

	const selection = useEntriesTableSelectionController({
		displayedRows: visibleEntryRows,
		rowSelection,
		selectionMode,
		setSelectedRowIds,
	});

	return {
		dateFilteredEntries,
		table,
		filteredRows,
		isMultiDate,
		selection,
	};
}
